import path from "node:path"
import { stripVTControlCharacters } from "node:util"
import { pathToFileURL } from "node:url"
import terminalLink from "terminal-link"

import { SETUP_INTRO_FRAME_INNER_WIDTH } from "./constants.js"

/** Minimum inner text column when the terminal is very narrow. */
const SETUP_FRAME_INNER_MIN = 50
/**
 * Horizontal padding inside the frame (character cells per side). Vertical inset uses fewer
 * full lines so it matches the perceived weight of the side gutters (a line is taller than a column).
 */
const DEFAULT_INNER_PADDING = 2

function innerVerticalLineCount(horizontalPad: number): number {
  if (horizontalPad < 1) {
    return 0
  }

  // 2 side spaces read lighter than 2 full blank lines; n spaces pair with (n−1) lines, min 1.
  return Math.max(1, horizontalPad - 1)
}

/** Blank lines before and after header art (outside the frame). */
const HEADER_ART_BLANK_LINES = 1

/**
 * One shared content-column width for framed panels: matches the first setup intro
 * (see {@link SETUP_INTRO_FRAME_INNER_WIDTH}) on wide terminals, never wider than the
 * terminal (unless very wide art in a given panel forces a larger box in `wrapFramedPanel`).
 */
export function resolveFrameContentColumnWidth(input: {
  minContentWidth?: number
  innerSidePadding: number
  columns?: number
}): number {
  const cols = input.columns ?? process.stdout.columns ?? 100
  const p = input.innerSidePadding
  const margin = 2 + 2 * p
  const maxByTerm = Math.max(SETUP_FRAME_INNER_MIN, cols - margin)
  const asIntro = Math.min(SETUP_INTRO_FRAME_INNER_WIDTH, maxByTerm)
  return Math.max(input.minContentWidth ?? SETUP_FRAME_INNER_MIN, asIntro)
}

export type Tone =
  | "amber"
  | "blue"
  | "blueBold"
  | "blueDark"
  | "cyan"
  | "green"
  | "greenBold"
  | "yellow"
  | "yellowBold"
  | "gray"
  | "bold"
  | "none"

export type RenderPanelLineTone = (line: string, lineIndex: number) => Tone

export type RenderFramedLinesOptions = {
  minContentWidth?: number
  /**
   * Horizontal spaces between the side borders and text (default: 2). Top/bottom blank lines
   * are derived from that so vertical insets do not look larger than horizontal.
   */
  innerSidePadding?: number
  /** Optional art centered above the frame. */
  headerArt?: string[]
  /** Color for header art. Defaults to "blue" if omitted. */
  headerArtTone?: Tone
  lineTone?: RenderPanelLineTone
}

export type RenderPanelOptions = {
  lineTone?: RenderPanelLineTone
  minContentWidth?: number
  /**
   * Horizontal spaces between the side borders and text (default: 2). Vertical padding is
   * derived so it does not look heavier than the horizontal gutter.
   */
  innerSidePadding?: number
  /** Tone for the panel title row(s). Defaults to bold. */
  titleTone?: Tone
  /** Optional multi-line ASCII art shown centered above the frame (e.g. first setup screen). */
  headerArt?: string[]
  /** Color for header art. Defaults to "blue". */
  headerArtTone?: Tone
}

export function colorize(text: string, tone: Tone): string {
  if (tone === "none") return text
  const codes: Record<Tone, string> = {
    // Warm celebratory gold — distinct from warning (`yellow` / \u001B[33m)
    amber: "\u001B[38;2;255;215;120m",
    bold: "\u001B[1m",
    blue: "\u001B[34m",
    blueBold: "\u001B[1;34m",
    blueDark: "\u001B[38;2;0;64;150m",
    cyan: "\u001B[36m",
    gray: "\u001B[90m",
    green: "\u001B[32m",
    greenBold: "\u001B[1;32m",
    none: "",
    yellow: "\u001B[33m",
    yellowBold: "\u001B[1;38;2;200;140;20m",
  }
  return `${codes[tone]}${text}\u001B[0m`
}

export function headline(text: string): string {
  return colorize(text, "bold")
}

export function muted(text: string): string {
  return colorize(text, "gray")
}

export function success(text: string): string {
  return `${colorize("✓", "green")} ${text}`
}

export function warning(text: string): string {
  return `${colorize("!", "yellow")} ${text}`
}

export function emphasis(text: string): string {
  return colorize(text, "blue")
}

export function renderStepHeader(title: string): string[] {
  return [`${colorize("■", "blue")} ${headline(`✨ ${title}`)}`, muted("─".repeat(Math.max(20, title.length + 4)))]
}

export function formatFullSnapshotPathLink(snapshotRoot: string): string {
  return formatLocalPathLink(snapshotRoot)
}

export function formatLocalPathLink(targetPath: string): string {
  const normalizedPath = path.resolve(targetPath)
  const fileUrl = pathToFileURL(normalizedPath).href
  return terminalLink(normalizedPath, fileUrl, { fallback: text => text })
}

function centerToVisibleWidth(text: string, targetWidth: number): string {
  const w = visibleWidth(text)
  if (w >= targetWidth) {
    return text
  }

  const pad = targetWidth - w
  const left = Math.floor(pad / 2)
  const right = pad - left
  return `${" ".repeat(left)}${text}${" ".repeat(right)}`
}

type FramedPanelExtras = {
  headerArt?: string[]
  headerArtTone?: Tone
}

// eslint-disable-next-line max-params -- frame builder needs width, padding, and tone context together
function wrapFramedPanel(
  leadingRows: Array<{ segment: string; tone: Tone }>,
  lines: string[],
  maxTerminalWidth: number,
  lineTone: RenderPanelLineTone | undefined,
  extras: FramedPanelExtras | undefined,
  innerSidePadding: number,
  /** Target content-column width; rows are already wrapped to this. */
  targetContentWidth: number,
): string {
  const innerPadding = innerSidePadding
  const wrappedLines = lines.flatMap((line, lineIndex) => {
    const tone: Tone = lineTone ? lineTone(line, lineIndex) : lineIsListItem(line) ? "gray" : "none"
    return wrapLine(line, maxTerminalWidth).map(segment => ({ segment, tone }))
  })
  const art = extras?.headerArt
  const artMaxW = art && art.length > 0 ? Math.max(...art.map(line => visibleWidth(line))) : 0
  const minInnerForArt = artMaxW > 0 ? Math.max(0, artMaxW - 2 * innerPadding - 2) : 0
  // One width for every step: match intro target, only grow for wide header art (not for long text lines).
  const width = Math.max(targetContentWidth, minInnerForArt, 30)
  const allRows = reflowRowsToFrameWidth([...leadingRows, ...wrappedLines], width)
  // Light box: between corners, length = width (text column) + 2*inner padding (row uses pad + text + pad).
  const betweenCorners = width + 2 * innerPadding
  const horiz = "\u2500".repeat(betweenCorners)
  const topBorderPlain = `\u250C${horiz}\u2510`
  const frameDisplayWidth = visibleWidth(topBorderPlain)
  const topBorder = colorize(topBorderPlain, "blue")
  const bottomBorder = colorize(`\u2514${horiz}\u2518`, "blue")
  const pad = " ".repeat(innerPadding)
  const row = (value = "", tone: Tone = "none") =>
    `${colorize("\u2502", "blue")}${pad}${colorize(padToVisibleWidth(value, width), tone)}${pad}${colorize("\u2502", "blue")}`
  const bodyText = allRows.map(item => row(item.segment, item.tone))
  const vCount = innerVerticalLineCount(innerPadding)
  const vPad = Array.from({ length: vCount }, () => row("", "none"))
  const body = [...vPad, ...bodyText, ...vPad]
  const headerArtTone: Tone = extras?.headerArtTone ?? "blue"
  const artColored =
    art && art.length > 0 ? art.map(line => colorize(centerToVisibleWidth(line, frameDisplayWidth), headerArtTone)) : []
  const artPrefix = artColored.length > 0 ? Array.from({ length: HEADER_ART_BLANK_LINES }, () => "") : []
  const artSuffix = artColored.length > 0 ? Array.from({ length: HEADER_ART_BLANK_LINES }, () => "") : []
  return [...artPrefix, ...artColored, ...artSuffix, topBorder, ...body, bottomBorder].join("\n")
}

function panelFraming(options: { minContentWidth?: number; innerSidePadding?: number } | undefined): {
  contentColumnWidth: number
  innerSidePadding: number
} {
  const innerSidePadding = options?.innerSidePadding ?? DEFAULT_INNER_PADDING
  return {
    contentColumnWidth: resolveFrameContentColumnWidth({
      minContentWidth: options?.minContentWidth,
      innerSidePadding,
    }),
    innerSidePadding,
  }
}

export function renderPanel(title: string, lines: string[], options?: RenderPanelOptions): string {
  const { contentColumnWidth, innerSidePadding } = panelFraming(options)
  const titleTone: Tone = options?.titleTone ?? "bold"
  const wrappedTitle = wrapLine(title, contentColumnWidth).map(segment => ({
    segment,
    tone: titleTone,
  }))
  return wrapFramedPanel(
    wrappedTitle,
    lines,
    contentColumnWidth,
    options?.lineTone,
    {
      headerArt: options?.headerArt,
      headerArtTone: options?.headerArtTone,
    },
    innerSidePadding,
    contentColumnWidth,
  )
}

/** Bordered panel with body lines only (no inner title row). */
export function renderFramedLines(lines: string[], options?: RenderFramedLinesOptions): string {
  const { contentColumnWidth, innerSidePadding } = panelFraming(options)
  return wrapFramedPanel(
    [],
    lines,
    contentColumnWidth,
    options?.lineTone,
    {
      headerArt: options?.headerArt,
      headerArtTone: options?.headerArtTone,
    },
    innerSidePadding,
    contentColumnWidth,
  )
}

function lineIsListItem(line: string): boolean {
  const trimmed = line.trimStart()
  return trimmed.startsWith("- ") || /^\d+\.\s/.test(trimmed)
}

function lineHasTerminalHyperlink(line: string): boolean {
  return line.includes("\u001B]8")
}

/** Re-wrap any row segment so no line is wider than the shared frame (links become plain if still too long). */
function reflowRowsToFrameWidth(
  rows: Array<{ segment: string; tone: Tone }>,
  maxContentWidth: number,
): Array<{ segment: string; tone: Tone }> {
  return rows.flatMap(row => {
    if (visibleWidth(row.segment) <= maxContentWidth) {
      return [row]
    }

    if (lineHasTerminalHyperlink(row.segment)) {
      const plain = stripVTControlCharacters(row.segment)
      return wrapTextToWidth(plain, maxContentWidth).map(s => ({ segment: s, tone: row.tone }))
    }

    return wrapTextToWidth(row.segment, maxContentWidth).map(s => ({ segment: s, tone: row.tone }))
  })
}

function wrapLine(line: string, width: number): string[] {
  if (visibleWidth(line) <= width) {
    return [line]
  }

  if (!line.trim()) {
    return [""]
  }

  if (lineHasTerminalHyperlink(line)) {
    return [line]
  }

  return wrapTextToWidth(line, width)
}

function wrapTextToWidth(line: string, width: number): string[] {
  if (visibleWidth(line) <= width) {
    return [line]
  }

  if (!line.trim()) {
    return [""]
  }

  const parts: string[] = []
  let remaining = line
  while (remaining.length > width) {
    const chunk = remaining.slice(0, width + 1)
    const breakAt = Math.max(
      chunk.lastIndexOf(" "),
      chunk.lastIndexOf("/"),
      chunk.lastIndexOf("\\"),
      chunk.lastIndexOf("-"),
    )
    const splitIndex = breakAt > 12 ? breakAt : width
    parts.push(remaining.slice(0, splitIndex).trimEnd())
    remaining = remaining.slice(splitIndex).trimStart()
  }

  if (remaining.length > 0) {
    parts.push(remaining)
  }

  return parts
}

function padToVisibleWidth(value: string, width: number): string {
  const padding = Math.max(0, width - visibleWidth(value))
  return `${value}${" ".repeat(padding)}`
}

function visibleWidth(value: string): number {
  const strippedValue = stripVTControlCharacters(value)
  let width = 0

  for (const char of Array.from(strippedValue)) {
    if (char === "\u200D" || char === "\uFE0E" || char === "\uFE0F") {
      continue
    }

    if (/[\p{Mark}\p{Control}]/u.test(char)) {
      continue
    }

    width += isWideCharacter(char) ? 2 : 1
  }

  return width
}

function isWideCharacter(char: string): boolean {
  if (char === "\u2139" || char === "\u23F1") {
    return true
  }

  return /[\p{Extended_Pictographic}\u1100-\u115F\u2329\u232A\u2E80-\uA4CF\uAC00-\uD7A3\uF900-\uFAFF\uFE10-\uFE19\uFE30-\uFE6F\uFF00-\uFF60\uFFE0-\uFFE6]/u.test(
    char,
  )
}
